<template>
  <div class="settings-gdpr">
    <sticky-header
      :title="$t('settingsGdpr.title')"
      :helpID="316">
      <btn
        type="success"
        slot="ui"
        @click.native="save">
        {{ $t('common.ui.save') }}
      </btn>

      <btn
        url="/settings"
        slot="ui">
        {{ $t('common.ui.back') }}
      </btn>
    </sticky-header>

    <block
      :isLoading="!configLoaded"
      :isSaving="saveInProgress"
      :title="$t('settingsGdpr.supportAccessTitle')">
      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.getAgreementsForSupport') }}
        </label>

        <switcher
          v-model="supportAgree"
          key="support-agree-switcher"
          slot="field" />

        <small
          class="note"
          slot="note">
          {{ $t('settingsGdpr.getAggrementsForSupportNote') }}
        </small>
      </field>
    </block>

    <block
      :isLoading="!configLoaded"
      :isSaving="saveInProgress"
      :title="$t('settingsGdpr.agreementsTitle')">
      <field slot="content">
        <label slot="label">
         {{ $t('settingsGdpr.tosUrl') }}
        </label>

        <input
          slot="field"
          v-model="termsUrl[currentLanguages.terms_url]"
          @keyup="clearError('terms-privacy-url-empty')" />

        <language-switcher
          slot="field"
          relatedTo="terms_url" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.privacyPolicyUrl') }}
        </label>

        <input
          slot="field"
          v-model="privacyPolicyUrl[currentLanguages.privacy_policy_url]"
          @keyup="clearError('terms-privacy-url-empty')" />

        <language-switcher
          slot="field"
          relatedTo="privacy_policy_url" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.gdprUrl') }}
        </label>

        <input
          slot="field"
          v-model="informationAgreeUrl[currentLanguages.information_agree_url]" />

        <language-switcher
          slot="field"
          relatedTo="information_agree_url" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.showCheckboxForTosOrPrivacyPolicy') }}
        </label>

        <switcher
          v-model="showTpAgree"
          key="show-tp-agree-switcher"
          slot="field"
          :class="{ 'is-invalid': this.errors.indexOf('terms-privacy-url-empty') > -1 && showTpAgree }" />

        <small
          v-if="this.errors.indexOf('terms-privacy-url-empty') > -1 && showTpAgree"
          class="note is-invalid"
          slot="note">
          {{ $t('settingsGdpr.ifItIsEnabledYouHaveToProvideAtLeastOneUrl') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.showInFormShortGdprInfo') }}
        </label>

        <switcher
          v-model="showInformationAgree"
          key="show-information-agree-switcher"
          slot="field" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.getAgreementsForEmails') }}
        </label>

        <switcher
          v-model="newsletterAgree"
          key="newsletter-agree-switcher"
          slot="field" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.getAgreementsForPhoneCalls') }}
        </label>

        <switcher
          v-model="telemarketingAgree"
          key="telemarketing-agree-switcher"
          slot="field" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.gdprInfoText') }}
        </label>

        <textarea
          v-model="informationAgreeText[currentLanguages.information_agree_text]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="information_agree_text" />

        <small
          v-if="this.errors.indexOf('terms-information-agree-empty') > -1 && showInformationAgree"
          class="note is-invalid"
          slot="note">
          {{ $t('settingsGdpr.ifInformationAgreeIsEnabledThisCannotBeEmpty') }}
        </small>

        <small
          slot="note"
          class="note">
          {{ $t('settingsGdpr.additionalGdprText') }}
        </small>
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.emailAgreementText') }}
        </label>

        <textarea
          v-model="newsletterAgreeText[currentLanguages.newsletter_agree_text]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="newsletter_agree_text" />
      </field>

      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.phoneCallsAgreementText') }}
        </label>

        <textarea
          v-model="telemarketingAgreeText[currentLanguages.telemarketing_agree_text]"
          slot="field"></textarea>

        <language-switcher
          slot="field"
          relatedTo="telemarketing_agree_text" />
      </field>
    </block>

    <block
      :isLoading="!configLoaded"
      :isSaving="saveInProgress"
      :title="$t('settingsGdpr.dataRetentionTitle')">
      <field slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.removeOldData') }}
        </label>

        <select
          v-model="removeOldData"
          slot="field">
          <option key="remove-old-data-0" :value="0">
            {{ $t('settingsGdpr.removeOldDataOptions.notRemove') }}
          </option>
          <option key="remove-old-data-1" :value="1">
            {{ $t('settingsGdpr.removeOldDataOptions.onlyReservations') }}
          </option>
          <option key="remove-old-data-2" :value="2">
            {{ $t('settingsGdpr.removeOldDataOptions.customersAndReservations') }}
          </option>
        </select>

        <small
          v-if="removeOldData === 1"
          class="note"
          slot="note">
          {{ $t('settingsGdpr.removeDataNote') }}
        </small>
      </field>

      <field
        v-if="removeOldData === 1 || removeOldData === 2"
        slot="content">
        <label slot="label">
          <template v-if="removeOldData === 1">
            {{ $t('settingsGdpr.removeDataAfterDaysReservations') }}
          </template>
          <template v-else-if="removeOldData === 2">
            {{ $t('settingsGdpr.removeDataAfterDaysCustomerAndReservations') }}
          </template>
        </label>

        <input
          slot="field"
          type="number"
          v-model="removeDataAfterDays"
          style="width: 100px;"
          :class="{
            'is-invalid': errors.indexOf('remove-data-after-days-empty') > -1 || errors.indexOf('remove-data-after-days-invalid') > -1
          }"
          @keyup="clearError('remove-data-after-days-empty');clearError('remove-data-after-days-invalid')" />
        <small
          v-if="removeOldData === 2"
          class="note"
          slot="note"
          v-pure-html="removeOldData === 2 && removeDataWithoutCustomer ? $t('settingsGdpr.removeDataAfterDaysCustomerAndReservationsNoteAlt') : $t('settingsGdpr.removeDataAfterDaysCustomerAndReservationsNote')">
        </small>

        <small
          v-if="errors.indexOf('remove-data-after-days-empty') > -1"
          class="note is-invalid"
          slot="note">
          {{ $t('settingsGdpr.valueCannotBeEmpty') }}
        </small>

        <small
          v-if="errors.indexOf('remove-data-after-days-invalid') > -1"
          class="note is-invalid"
          slot="note">
          {{ $t('settingsGdpr.valueInvalid') }}
        </small>
      </field>

      <field
        v-if="removeOldData === 2"
        slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.removeDataWithoutCustomer') }}
        </label>

        <switcher
          v-model="removeDataWithoutCustomer"
          key="remove-data-without-customer-switcher"
          slot="field" />

        <small
          class="note"
          slot="note">
          {{ $t('settingsGdpr.removeDataWithoutCustomerDesc') }}
        </small>
      </field>

      <field
        v-if="removeOldData === 2"
        slot="content">
        <label slot="label">
          {{ $t('settingsGdpr.removeDataByAgree') }}
        </label>

        <switcher
          v-model="removeDataByAgree"
          key="remove-data-by-agree-switcher"
          slot="field" />
      </field>
    </block>

    <block
      v-if="$root.appLanguage === 'pl'"
      :title="$t('settingsGdpr.documentsTitle')"
      :noPadding="true">
      <p
        class="document-download"
        slot="content">
        {{ $t('settingsGdpr.templateForGdprInfo') }}
        <btn url="https://bookero.pl/download/regulations/obowiazek_informacyjny.docx">
          {{ $t('settingsGdpr.downloadDocument') }}
        </btn>
      </p>

      <p
        class="document-download"
        slot="content">
        {{ $t('settingsGdpr.templateForDPA') }}

        <btn url="https://bookero.pl/download/regulations/umowa_powierzenia_przetwarzania_danych_osobowych.docx">
          {{ $t('settingsGdpr.downloadDocument') }}
        </btn>
      </p>

      <p
        class="document-download"
        slot="content">
        {{ $t('settingsGdpr.templateForRemoteWork') }}
        <btn url="https://bookero.pl/download/regulations/zgoda_na_prace_zdalna.doc">
          {{ $t('settingsGdpr.downloadDocument') }}
        </btn>
      </p>

      <p
        class="document-download"
        slot="content">
        {{ $t('settingsGdpr.templateForDataProcessing') }}
        <btn url="https://bookero.pl/download/regulations/upowaznienie_do_przetwarzania_danych.doc">
          {{ $t('settingsGdpr.downloadDocument') }}
        </btn>
      </p>

      <p
        class="document-download"
        slot="content">
        {{ $t('settingsGdpr.templateForImageAgreement') }}
        <btn url="https://bookero.pl/download/regulations/zgoda_na_wykorzystanie_wizerunku.doc">
          {{ $t('settingsGdpr.downloadDocument') }}
        </btn>
      </p>

      <small
        class="document-disclaimer"
        slot="content"
        v-pure-html="$t('settingsGdpr.gdprDisclaimer')">
      </small>
    </block>
  </div>
</template>

<script>
import Block from './../common/Block';
import ErrorUtils from './../helpers/ErrorUtils.js';
import FormHelpers from './../mixins/FormHelpers';
import Languages from './../mixins/Languages';
import SettingsBasicBlock from './../ui/SettingsBasicBlock';
import StickyHeader from './../common/StickyHeader';

export default {
  name: 'settings-gdpr',
  mixins: [
    FormHelpers,
    Languages
  ],
  components: {
    'block': Block,
    'settings-basic-block': SettingsBasicBlock,
    'sticky-header': StickyHeader
  },
  data () {
    return {
      errors: [],
      termsUrl: '',
      privacyPolicyUrl: '',
      informationAgreeUrl: '',
      showTpAgree: false,
      showInformationAgree: false,
      newsletterAgree: false,
      telemarketingAgree: false,
      informationAgreeText: '',
      newsletterAgreeText: '',
      telemarketingAgreeText: '',
      supportAgree: false,
      removeOldData: 0,
      removeDataAfterDays: 365,
      removeDataByAgree: true,
      removeDataWithoutCustomer: false,
      translations: {},
      currentLanguages: {
        terms_url: '',
        privacy_policy_url: '',
        information_agree_url: '',
        information_agree_text: '',
        newsletter_agree_text: '',
        telemarketing_agree_text: ''
      },
      configLoaded: false,
      saveInProgress: false
    };
  },
  mounted () {
    if (this.$store.state.config.user.id !== 0) {
      this.loadData();
    }

    this.$bus.$on('configuration-loaded', this.loadData);
  },
  methods: {
    loadData () {
      this.$http.get('/panel/settings/rodo/item').then(response => {
        this.setFormData(response.data.data);
        this.configureMultilanguage();
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/settings/rodo/item', this.$router, this.$bus);
      });
    },
    configureMultilanguage () {
      this.currentLanguages.terms_url = this.defaultLanguageCode;
      this.currentLanguages.privacy_policy_url = this.defaultLanguageCode;
      this.currentLanguages.information_agree_url = this.defaultLanguageCode;
      this.currentLanguages.information_agree_text = this.defaultLanguageCode;
      this.currentLanguages.newsletter_agree_text = this.defaultLanguageCode;
      this.currentLanguages.telemarketing_agree_text = this.defaultLanguageCode;
      this.$bus.$on('change-field-language', this.changeCurrentLanguageForField);
    },
    setFormData (data) {
      this.translations = data.translations;
      this.configLoaded = true;

      if (!Object.keys(this.translations).length) {
        this.translations = this.createEmptyTranslationsObject([
          'terms_url',
          'privacy_policy_url',
          'information_agree_url',
          'information_agree_text',
          'newsletter_agree_text',
          'telemarketing_agree_text'
        ]);
      }

      this.termsUrl = this.getLanguageFieldValues(this.translations, 'terms_url', data.terms_url || '');
      this.privacyPolicyUrl = this.getLanguageFieldValues(this.translations, 'privacy_policy_url', data.privacy_policy_url || '');
      this.informationAgreeUrl = this.getLanguageFieldValues(this.translations, 'information_agree_url', data.information_agree_url || '');
      this.showTpAgree = data.show_tp_agree;
      this.showInformationAgree = data.show_information_agree;
      this.newsletterAgree = data.newsletter_agree;
      this.telemarketingAgree = data.telemarketing_agree;
      this.supportAgree = data.support_agree;
      this.informationAgreeText = this.getLanguageFieldValues(this.translations, 'information_agree_text', data.information_agree_text || '');
      this.newsletterAgreeText = this.getLanguageFieldValues(this.translations, 'newsletter_agree_text', data.newsletter_agree_text || '');
      this.telemarketingAgreeText = this.getLanguageFieldValues(this.translations, 'telemarketing_agree_text', data.telemarketing_agree_text || '');
      this.removeOldData = data.remove_old_data;
      this.removeDataAfterDays = data.remove_data_after_days;
      this.removeDataByAgree = data.remove_data_by_agree;
      this.removeDataWithoutCustomer = data.remove_data_without_customer;
    },
    validate () {
      this.errors = [];

      if (
        this.showTpAgree &&
        this.termsUrl[this.currentLanguages.terms_url].trim() === '' &&
        this.privacyPolicyUrl[this.currentLanguages.privacy_policy_url].trim() === ''
      ) {
        this.errors.push('terms-privacy-url-empty');
      }

      if (this.showInformationAgree && !this.informationAgreeText[this.defaultLanguageCode]) {
        this.errors.push('terms-information-agree-empty');
      }

      if (this.removeOldData === 1 || this.removeOldData === 2) {
        if (!this.removeDataAfterDays) {
          this.errors.push('remove-data-after-days-empty');
        } else if (this.removeDataAfterDays < 1) {
          this.errors.push('remove-data-after-days-invalid');
        }
      }
    },
    save () {
      this.validate();

      if (this.errors.length) {
        this.$bus.$emit('notification-display', {
          message: this.$t('common.ui.invalidFields'),
          type: 'error'
        });

        this.handleInvalidFields();

        return;
      }

      this.saveInProgress = true;
      this.saveForm();
    },
    saveForm () {
      let formData = this.prepareFormData();
      formData.settings = 'rodo';

      this.$http.post('/panel/settings/rodo/save', formData).then(response => {
        if (response.data.success) {
          this.$bus.$emit('notification-display', {
            message: this.$t('settingsGdpr.settingsSaved'),
            type: 'success'
          });

          this.$store.commit('config/setCompanyRodoConfig', this.prepareFormData());
          this.$router.push('/settings');
          this.saveInProgress = false;
        } else {
          this.$bus.$emit('notification-display', {
            message: this.$t('settingsGdpr.settingsNotSaved'),
            type: 'error'
          });
        }
      }).catch(error => {
        ErrorUtils.handleSubmitError(error, '/panel/settings/rodo/save', this.$router, this.$bus, this.saveForm.bind(this));
        this.saveInProgress = false;
      });
    },
    prepareFormData () {
      let formData = {
        terms_url: this.termsUrl[this.defaultLanguageCode],
        privacy_policy_url: this.privacyPolicyUrl[this.defaultLanguageCode],
        information_agree_url: this.informationAgreeUrl[this.defaultLanguageCode],
        show_tp_agree: this.showTpAgree,
        show_information_agree: this.showInformationAgree,
        newsletter_agree: this.newsletterAgree,
        telemarketing_agree: this.telemarketingAgree,
        support_agree: this.supportAgree,
        information_agree_text: this.informationAgreeText[this.defaultLanguageCode],
        newsletter_agree_text: this.newsletterAgreeText[this.defaultLanguageCode],
        telemarketing_agree_text: this.telemarketingAgreeText[this.defaultLanguageCode],
        remove_old_data: parseInt(this.removeOldData, 10)
      };

      if (this.removeOldData === 1) {
        formData.remove_data_after_days = parseInt(this.removeDataAfterDays, 10);
        formData.remove_data_without_customer = !!this.removeDataWithoutCustomer;
      } else if (this.removeOldData === 2) {
        formData.remove_data_after_days = parseInt(this.removeDataAfterDays, 10);
        formData.remove_data_without_customer = !!this.removeDataWithoutCustomer;
        formData.remove_data_by_agree = !!this.removeDataByAgree;
      }

      if (this.languagesAreAvailable) {
        formData.translations = this.prepareTranslationsObject({
          terms_url: this.termsUrl,
          privacy_policy_url: this.privacyPolicyUrl,
          information_agree_url: this.informationAgreeUrl,
          information_agree_text: this.informationAgreeText,
          newsletter_agree_text: this.newsletterAgreeText,
          telemarketing_agree_text: this.telemarketingAgreeText
        });
      }

      return formData;
    }
  },
  beforeDestroy () {
    this.$bus.$off('change-field-language', this.changeCurrentLanguageForField);
    this.$bus.$off('configuration-loaded', this.loadData);
  }
};
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.settings-gdpr {
  padding: 0 0 2rem 0;
  width: 100%;

  textarea {
    height: 160px;
    width: calc(66.666666% - 50px)!important;
  }

  .document-download,
  .document-disclaimer {
    padding: 2rem 3rem;
    width: 100%;
  }

  .document-download {
    align-items: center;
    border-bottom: 1px solid $color-border-light;
    display: flex;
    margin: 0;

    .button {
      margin: 0 0 0 auto;
    }
  }

  .document-disclaimer {
    padding: 3rem;
  }

  .note /deep/ .is-invalid {
    color: $color-warning;
  }
}

@media (max-width: 1100px) {
  .settings-gdpr {
    .field {
      input,
      select,
      textarea {
        width: calc(66% - 50px)!important;
      }
    }
  }
}

@media (max-width: 640px) {
  .settings-gdpr {
    margin-top: 6rem;

    .document-download,
    .document-disclaimer {
      padding: 2rem 1rem;
    }
  }
}

@media (max-width: 580px) {
  .settings-gdpr {
    .field {
      label,
      input,
      select,
      textarea {
        text-align: left;
        width: calc(100% - 50px)!important;
      }

      label {
        margin-bottom: .5rem;
      }

      small.note {
        padding: .5rem 0 0 0;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/SettingsGdpr.vue